import { createContext, useContext, useMemo } from 'react';
import createStore, { StoreApi, UseBoundStore } from 'zustand';
import shallow from 'zustand/shallow';
import { produce } from 'immer';
import debounce from 'lodash/debounce';
import { Block, BlockResponseObject } from '@/lib/types';

interface FillableBlocksState {
  blocks: Block[];
  payloadByBlock: BlockResponseObject;

  initialize(args: {
    blocks: Block[];
    responses: BlockResponseObject;
    updateResponseOnServer(args: { block: Block; payload: any }): void;
  }): void;
  setResponsePayload(referenceID: string, payload: any): void;
  updateResponseOnServer(args: { block: Block; payload: any }): void;
}
export const createFillableBlocksStore = () =>
  createStore<FillableBlocksState>((set) => ({
    blocks: [],
    payloadByBlock: {},

    initialize: ({ blocks, responses, updateResponseOnServer }) =>
      set(
        produce((state) => {
          state.blocks = blocks;
          state.payloadByBlock = responses || {};
          state.updateResponseOnServer = updateResponseOnServer;
        }),
      ),

    setResponsePayload: (referenceID, payload) =>
      set(
        produce((state) => {
          state.payloadByBlock[referenceID] = payload;
        }),
      ),

    updateResponseOnServer: () => void 0,
  }));

type FormStore = UseBoundStore<FillableBlocksState, StoreApi<FillableBlocksState>>;

export const FormStoreContext = createContext<FormStore | null>(null);

export function useFormStore() {
  const useFillableBlocksStore = useContext(FormStoreContext);
  if (!useFillableBlocksStore) {
    throw new Error('Form store not being provided');
  }
  return useFillableBlocksStore;
}

export function useFillableBlockResponse({ block }: { block: Block }) {
  const useFillableBlocksStore = useFormStore();

  const { setResponsePayload, payload, updateResponseOnServer } = useFillableBlocksStore(
    (state) => ({
      setResponsePayload: state.setResponsePayload,
      payload: state.payloadByBlock[block.referenceID],
      updateResponseOnServer: state.updateResponseOnServer,
    }),
    shallow,
  );

  const debounceUpdate = useMemo(() => {
    return debounce(updateResponseOnServer, 500);
  }, [updateResponseOnServer]);

  function updatePayload(newPayload: any) {
    setResponsePayload(block.referenceID, newPayload);
    debounceUpdate({ block, payload: newPayload });
  }

  return { updatePayload, payload };
}
